import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { Button, Input, TextArea, toast } from '../../components/common';
import { catalogApi } from '../../services/api';

export default function CategoryForm({ category, onSuccess, onCancel }) {
    const isEditing = !!category;

    const [form, setForm] = useState({
        code: '',
        name: '',
        description: ''
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (category) {
            setForm({
                code: category.code || '',
                name: category.name || '',
                description: category.description || ''
            });
        }
    }, [category]);

    const mutation = useMutation({
        mutationFn: (data) => isEditing
            ? catalogApi.updateCategory(category.id, data)
            : catalogApi.createCategory(data),
        onSuccess: () => {
            toast.success(`Category ${isEditing ? 'updated' : 'created'} successfully`);
            onSuccess?.();
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to save category');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.code) newErrors.code = 'Code is required';
        if (!form.name) newErrors.name = 'Name is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        mutation.mutate(form);
    };

    return (
        <form onSubmit={handleSubmit} className="category-form">
            <div className="form-stack">
                <Input
                    name="code"
                    label="Category Code"
                    value={form.code}
                    onChange={handleChange}
                    error={errors.code}
                    disabled={isEditing}
                    required
                />
                <Input
                    name="name"
                    label="Category Name"
                    value={form.name}
                    onChange={handleChange}
                    error={errors.name}
                    required
                />
                <TextArea
                    name="description"
                    label="Description"
                    value={form.description}
                    onChange={handleChange}
                    rows={3}
                />
            </div>

            <div className="form-actions">
                <Button variant="secondary" type="button" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="submit" loading={mutation.isPending}>
                    {isEditing ? 'Update Category' : 'Create Category'}
                </Button>
            </div>
        </form>
    );
}

CategoryForm.propTypes = {
    category: PropTypes.object,
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func
};
